// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";

// 남양주 서비스 카탈로그(B4 위젯) 카드 클릭 시 열리는 급여별 드릴다운.
// 이전에는 /organizations?service_code=... 로 이동만 했는데 그 화면이 급여 필터를
// 모르던 탓에 전체 기관 목록이 그대로 떴다(= 필터링 안 됨). 이제 카드 숫자를 만든
// 그 스코프(dashboards#region_service_catalog_items)를 그대로 페이징해 보여주고,
// "전체 화면에서 보기"도 실제로 필터된 /organizations 로 나간다.
//
// 탭:
//   기관   — 이 급여를 제공하는 권역 내 기관(카드의 "기관 N"과 동수)
//   의뢰   — 이 급여로 나간 의뢰(미응답만 보기 토글)

const PER_PAGE = 25;

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("ko-KR", { year: "2-digit", month: "short", day: "numeric" });
  } catch { return iso; }
}

export function ServiceCatalogDrilldownModal({ service, regionCode, initialView, onClose }) {
  useEscapeKey(onClose);
  // initialView: "orgs" | "referrals"(미응답 배지 → 미응답만) | "referrals-all"(이번주 의뢰 → 전체)
  const [view, setView] = React.useState(String(initialView || "").startsWith("referrals") ? "referrals" : "orgs");
  // 미응답 배지로 열었을 때는 미응답만 — 그 숫자를 눌렀으니 그 목록이어야 한다.
  const [onlyPending, setOnlyPending] = React.useState(initialView === "referrals");
  const [rows, setRows] = React.useState(null);
  const [pagination, setPagination] = React.useState({ page: 1, total: 0, total_pages: 1 });
  const [page, setPage] = React.useState(1);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => { setPage(1); }, [service?.code, view, onlyPending]);

  React.useEffect(() => {
    if (!service?.code) return;
    let alive = true;
    setErr(null);
    setRows(null);
    const params = new URLSearchParams({
      service_code: service.code, view, page: String(page), per_page: String(PER_PAGE),
    });
    if (view === "referrals" && onlyPending) params.set("only", "pending");
    // 급여 코드는 service_code — `code`는 권역 코드(?code=R019)라 겹치면 404가 난다.
    if (regionCode) params.set("code", regionCode);
    fetch(`/api/v1/dashboard/region/service_catalog/items?${params.toString()}`,
          { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(async r => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.message || j.error || `HTTP ${r.status}`);
        return j;
      })
      .then(data => {
        if (!alive) return;
        setRows(data.items || []);
        setPagination(data.pagination || { page: 1, total: 0, total_pages: 1 });
      })
      .catch(e => { if (alive) setErr(apiErrorMessage(e)); });
    return () => { alive = false; };
  }, [service?.code, regionCode, view, onlyPending, page]);

  const orgHref = `/organizations?service_code=${encodeURIComponent(service?.code || "")}`
    + `&service_label=${encodeURIComponent(service?.label || "")}`;

  const tab = (key, label) => (
    <button type="button" onClick={() => setView(key)}
      aria-pressed={view === key}
      className={view === key ? "btn btn-primary btn-sm" : "btn btn-ghost btn-sm"}>
      {label}
    </button>
  );

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="service-catalog-drilldown-title"
      style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:80, display:'grid', placeItems:'center', padding:24}}
      onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} style={{background:'var(--surface)', borderRadius:14, width:'min(720px, 100%)', maxHeight:'min(80vh, 720px)', display:'flex', flexDirection:'column', boxShadow:'0 18px 60px rgba(0,0,0,0.25)', overflow:'hidden'}}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px', borderBottom:'1px solid var(--border-soft)'}}>
          <div>
            <div id="service-catalog-drilldown-title" className="h3" style={{margin:0}}>
              {service?.label || "급여"}
              <span className="muted mono" style={{fontSize:11, fontWeight:600, marginLeft:8}}>{service?.code}</span>
            </div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>
              {rows === null
                ? "불러오는 중…"
                : pagination.total_pages > 1
                  ? `${view === "orgs" ? "제공 기관" : "의뢰"} ${pagination.total.toLocaleString()}건 · 페이지 ${pagination.page}/${pagination.total_pages}`
                  : `${view === "orgs" ? "제공 기관" : "의뢰"} ${pagination.total.toLocaleString()}건`}
            </div>
          </div>
          <button type="button" aria-label="닫기" onClick={onClose}
            style={{background:'transparent', border:'none', color:'var(--ink-3)', cursor:'pointer', padding:4}}>
            {Ic.close}
          </button>
        </div>

        <div style={{display:'flex', alignItems:'center', gap:8, padding:'10px 22px', borderBottom:'1px solid var(--border-soft)', flexWrap:'wrap'}}>
          {tab("orgs", "제공 기관")}
          {tab("referrals", "의뢰")}
          {view === "referrals" && (
            <label style={{display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--ink-2)', cursor:'pointer', marginLeft:4}}
                   title="아직 답이 오지 않은 의뢰(대기·연락)만 봅니다">
              <input type="checkbox" checked={onlyPending} onChange={e=>setOnlyPending(e.target.checked)} style={{cursor:'pointer'}}/>
              미응답만
            </label>
          )}
        </div>

        <div style={{flex:1, overflow:'auto'}}>
          {err && <div style={{padding:22, color:'var(--rose-500)', fontSize:13}}>목록을 불러오지 못했습니다: {err}</div>}
          {!err && rows && rows.length === 0 && (
            <div className="muted" style={{padding:'32px 22px', textAlign:'center', fontSize:12}}>
              {view === "orgs" ? "이 급여를 제공하는 기관이 없습니다." : "이 급여로 나간 의뢰가 없습니다."}
            </div>
          )}
          {!err && rows && rows.length > 0 && (
            <table className="tbl" style={{width:'100%'}}>
              <thead>
                <tr>
                  <th style={{paddingLeft:22}}>{view === "orgs" ? "기관" : "대상"}</th>
                  <th>{view === "orgs" ? "정보" : "기관"}</th>
                  <th>상태</th>
                  <th style={{paddingRight:22}}>{view === "orgs" ? "최근 보고" : "의뢰일"}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}
                    onClick={r.href ? () => { window.location.href = r.href; } : undefined}
                    style={{cursor: r.href ? 'pointer' : 'default'}}
                    title={r.href ? `${r.title} 상세 보기` : r.title}>
                    <td style={{paddingLeft:22}}><b>{r.title || "-"}</b></td>
                    <td><span className="muted" style={{fontSize:12}}>{r.subtitle || "-"}</span></td>
                    <td>
                      {r.badge
                        ? <span className={"badge badge-" + (r.badge.tone || "gray")}>{r.badge.label}</span>
                        : <span className="muted">-</span>}
                    </td>
                    <td className="mono" style={{fontSize:12, paddingRight:22}}>{fmtDate(r.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{padding:'12px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10}}>
          <a href={orgHref} className="btn btn-soft btn-md" style={{textDecoration:'none'}}
             title="이 급여를 제공하는 기관만 기관 마스터에서 봅니다">기관 마스터에서 보기 →</a>
          {pagination.total_pages > 1 && (
            <div style={{display:'flex', alignItems:'center', gap:8}}>
              <button type="button" className="btn btn-ghost btn-sm" disabled={pagination.page <= 1}
                onClick={()=>setPage(p => Math.max(1, p - 1))}>이전</button>
              <span className="muted" style={{fontSize:12}}>{pagination.page} / {pagination.total_pages}</span>
              <button type="button" className="btn btn-ghost btn-sm" disabled={pagination.page >= pagination.total_pages}
                onClick={()=>setPage(p => Math.min(pagination.total_pages, p + 1))}>다음</button>
            </div>
          )}
          <button type="button" className="btn btn-soft btn-md" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
